import { useMemo, useState } from "react";
import { gltfLoader, IGltfParsedData } from "@/filestore";
import { AnimationClip, FileLoader, Mesh, Object3D } from "three";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import * as utils from "three/examples/jsm/utils/SkeletonUtils.js";
import { convertGltf } from "../../utils/convertGltf";
import { disposeObject3D } from "../../utils/dispose-object3d";

type IPendingData = {
    status: 'pending';
    /** Resolves null if the load fails — it always settles, so nothing can suspend on it forever. */
    promise: Promise<IPreloadModelReturnData | null>;
}

type ICompleteData = {
    status: 'complete';
    gltf: GLTF;
    parsedData: IGltfParsedData;
    arrayBuffer: ArrayBuffer;
}

type IErrorData = {
    status: 'error';
    error: Error;
}

// global cache outside of react
export const modelCache: {
    [url: string]: IPendingData | ICompleteData | IErrorData
} = {};

// fill the global model cache or model error cache if not yet filled for url

export interface IPreloadModelReturnData {
    url: string;
    arrayBuffer: ArrayBuffer;
}

/**
 * Resolves `null` rather than rejecting on failure, and MUST always settle.
 *
 * Every error branch below writes the `error` cache entry that `useGLTFArrayBufferLoader` re-throws
 * into the render phase, where `EntityErrorBoundary` turns it into the broken-entity placeholder. It
 * must still settle the promise it lives in: `preloadGltf` awaits these, and the preloader's own
 * `settle()` guards rejection only — a promise that never settles leaves `Jute.loaded()` unfired and
 * the launch spinner turning forever.
 *
 * Null rather than a rejection because `preloadGltf` uses `Promise.all`: one rejection would discard
 * every model that loaded fine alongside the broken one.
 */
export const createModelLoadPromise = (
    url: string,
    onError?: () => unknown
): Promise<IPreloadModelReturnData | null> => {

    const modelData = modelCache[url];

    if (modelData?.status === 'pending') return modelData.promise;
    if (modelData?.status === 'complete') return Promise.resolve({ url, arrayBuffer: modelData?.arrayBuffer });
    // A url that already failed is not retried for the life of the session: the render path throws out
    // of the `error` entry before reaching here, and re-fetching would restart the same 404 per render.
    if (modelData?.status === 'error') return Promise.resolve(null);

    // we are using the custom gltfloader which always uses textureLoader instead of 
    // the imageBitmapLoader. Latter does not always release memory coorectly leading resulting in app crashing
    // (see https://discourse.threejs.org/t/error-with-the-gltfloader-unable-to-load-texture-blob/32465)
    const loadPromise = new Promise<IPreloadModelReturnData | null>(res => {
        const fail = () => {
            modelCache[url] = {
                status: 'error',
                error: new Error("model load error")
            }
            onError?.();
            res(null);
        };
        new FileLoader()
            .setResponseType('arraybuffer')
            .load(url, arrayBuffer => {
                if (typeof arrayBuffer === 'string') {
                    fail();
                    return;
                }

                gltfLoader.parse(arrayBuffer, '', gltf => {
                    if (modelCache[url]?.status !== 'complete') {
                        modelCache[url] = {
                            status: 'complete',
                            gltf,
                            parsedData: convertGltf(gltf),
                            arrayBuffer
                        };
                    }
                    res({ arrayBuffer, url });
                }, fail)
            },
                undefined,
                fail
            );
    });
    modelCache[url] = {
        status: 'pending',
        promise: loadPromise
    }
    return loadPromise;
};

/**
 * Drop every completed cache entry whose URL the project no longer references, disposing its GPU
 * resources.
 *
 * Purely additive: nothing calls this unless it opts in, so the Designer editor is unaffected.
 *
 * WHY A KEEP-SET RATHER THAN REFERENCE COUNTING. Exact refcounting would mean retain/release inside
 * `useGLTFLoader` / `useGLTFArrayBufferLoader`, hooks the editor also renders through. Filtering on "the
 * current content doc no longer mentions this URL" needs no changes to those hooks — but it is only safe
 * once React has unmounted the components that used them, so the caller MUST defer this until after the
 * store dispatch has flushed (and past the longest scene transition).
 *
 * `'pending'` entries are skipped deliberately: a suspended component is waiting on that promise, and its
 * resolution would write straight back into the cache.
 */
export const evictModelsNotIn = (keepUrls: Set<string>): string[] => {
    const evicted: string[] = [];
    // One shared set: `parsedData.scene` is a SkeletonUtils.clone() of `gltf.scene`, so the two share
    // geometries and materials (all but the baked geometry convertGltf builds per skinned mesh).
    const seen = new WeakSet<object>();

    for (const url of Object.keys(modelCache)) {
        const entry = modelCache[url];
        if (entry?.status !== 'complete' || keepUrls.has(url)) continue;
        disposeObject3D(entry.gltf.scene, seen);
        disposeObject3D(entry.parsedData.scene, seen);
        delete modelCache[url];
        evicted.push(url);
    }

    return evicted;
};

export const preloadGltf = (urls: string[]): Promise<IPreloadModelReturnData[] | null> => {
    // create array of promises
    const promises: Promise<IPreloadModelReturnData | null>[] = [];

    for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        if (!!modelCache[url]?.status) continue;
        const modelLoadPromise = createModelLoadPromise(url);
        promises.push(modelLoadPromise);
    }
    // Failed models resolve null and are dropped here, so callers never see one: the static-geometry
    // worker reads `.arrayBuffer` off every entry unguarded. `null` still means "no models at all",
    // which is a different answer from "every model failed" (an empty array).
    return promises.length
        ? Promise.all([...promises]).then(results => results.filter((r): r is IPreloadModelReturnData => r !== null))
        : new Promise((res) => res(null));
};

interface IGLTFLoaderOptions {
    castShadow?: boolean;
    receiveShadow?: boolean;
}

export const useGLTFArrayBufferLoader = (url: string, options?: IGLTFLoaderOptions): {
    scene: Object3D;
    animations: AnimationClip[];
    arrayBuffer: ArrayBuffer;
    isSkinned: boolean;
} => {
    const { castShadow, receiveShadow } = options ?? {};

    // state used to trigger rerender in order to throw async errors into render phase
    const [_, triggerRender] = useState(0);


    const modelData = modelCache[url];

    // return model immediately if in cache
    if (modelData?.status === 'complete') {
        const {
            scene: modelScene,
            animations,
        } = modelData.gltf;

        return useMemo(() => {
            // use Skeleton utils to safely clone skinned mesh (and other) models
            const clonedScene = utils.clone(modelScene) as Object3D;
            let isSkinned = false;

            // add render order to mesh if material(s) are transparent
            clonedScene.traverse(n => {
                const node = n as Mesh & {isSkinnedMesh: boolean};
                if (node.isSkinnedMesh || node.isMesh) {
                    if (node.isSkinnedMesh) isSkinned = true;
                    if (castShadow) node.castShadow = true;
                    if (receiveShadow) node.receiveShadow = true;
                }
            });

            clonedScene.updateMatrixWorld(true);

            return { scene: clonedScene, animations, isSkinned, arrayBuffer: modelData.arrayBuffer }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [url, modelScene, animations, castShadow, receiveShadow])

    }

    // if async error throw error into render phase (triggered via triggerRender)
    if (modelData?.status === 'error') {
        throw modelData.error;
        // delete globalErrorCache[url];
        // throw error;
    }

    // throw promise for suspense to catch and also trigger re-render once error
    // occurs to throw async error in new render phase
    throw createModelLoadPromise(url, () => {
        console.log("prelading error");
        triggerRender((prevState) => prevState + 1);
    });
};
